
import { BlogPost } from "@/types/blog";
import { supabase } from "@/integrations/supabase/client";
import { generateMarkdownFromPost } from "./wordpressMarkdownGenerator";
import { processImage } from "./imageProcessing";
import { toast } from "sonner";

/**
 * Saves a blog post to the database
 * @param post The blog post to save
 * @param isDraft Whether to save the post as a draft
 * @returns The saved blog post
 */
export const saveBlogPost = async (post: BlogPost, isDraft: boolean = false): Promise<BlogPost> => {
  try {
    console.log('Saving blog post:', post.title, 'with cover image:', post.coverImage ? 'yes' : 'no');
    
    const { data, error } = await supabase
      .from('blog_posts')
      .upsert({
        title: post.title,
        slug: post.slug,
        content: post.content,
        post_summary: post.excerpt,
        featured_image: post.coverImage || null,
        author: post.author || null,
        publish_date: new Date(post.date).toISOString(),
        is_published: !isDraft,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }, { onConflict: 'slug' })
      .select();
    
    if (error) {
      console.error('Error saving blog post:', error);
      throw error;
    }
    
    if (post.tags && post.tags.length > 0) {
      await processAndSaveTags(data[0].id, post.tags);
    }
    
    // If there's image attribution data, store it separately
    if (post.imageAttribution) {
      try {
        // Insert or update image attribution data using direct table operation
        const { error: attributionError } = await supabase
          .from('blog_image_attribution')
          .upsert({
            post_id: data[0].id,
            photographer: post.imageAttribution.photographer || null,
            photographer_url: post.imageAttribution.photographerUrl || null
          }, { 
            onConflict: 'post_id' 
          });
        
        if (attributionError) {
          console.error('Error saving image attribution:', attributionError);
          // Continue even if attribution saving fails
        } else {
          console.log('Successfully saved image attribution for:', post.title);
        }
      } catch (attributionError) {
        console.error('Error saving image attribution:', attributionError);
        // Continue even if attribution saving fails
      }
    }
    
    console.log(`Blog post "${post.title}" saved successfully as ${isDraft ? "draft" : "published"}`);
    return post;
  } catch (error) {
    console.error('Error in saveBlogPost:', error);
    throw error;
  }
};

/**
 * Process and save tags for a blog post
 * @param postId The ID of the blog post
 * @param tags Array of tags to process and save
 */
const processAndSaveTags = async (postId: number, tags: string[]): Promise<void> => {
  try {
    for (const tagName of tags) {
      // Check if the tag exists
      const { data: existingTags } = await supabase
        .from('blog_tags')
        .select('id')
        .eq('name', tagName)
        .limit(1);
      
      let tagId;
      
      if (existingTags && existingTags.length > 0) {
        tagId = existingTags[0].id;
      } else {
        // Create the tag
        const { data: newTag } = await supabase
          .from('blog_tags')
          .insert({ name: tagName })
          .select('id');
        
        if (newTag) {
          tagId = newTag[0].id;
        }
      }
      
      if (tagId) {
        // Create the relationship
        await supabase
          .from('blog_post_tags')
          .upsert({ post_id: postId, tag_id: tagId }, { onConflict: 'post_id,tag_id' });
      }
    }
  } catch (error) {
    console.error('Error processing tags:', error);
    // Continue even if tag processing fails
  }
};

/**
 * Delete a blog post from the database
 * @param slug The slug of the post to delete
 * @returns boolean indicating success
 */
export const deleteBlogPost = async (slug: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('blog_posts')
      .delete()
      .eq('slug', slug);
    
    if (error) {
      console.error('Error deleting blog post:', error);
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Error in deleteBlogPost:', error);
    throw error;
  }
};

/**
 * Generate static blog files from WordPress posts
 * @param posts Array of blog posts
 * @returns Array of generated file paths
 */
export const generateStaticBlogFiles = async (posts: BlogPost[]): Promise<string[]> => {
  const generatedFiles: string[] = [];
  
  for (const post of posts) {
    try {
      // Generate markdown content
      const markdownContent = generateMarkdownFromPost(post);
      
      // In a real Node.js environment, we would write this to files
      // For browser environment, we'll just keep track of what would be generated
      generatedFiles.push(`content/blog/${post.slug}.md`);
      
      console.log(`Generated static file for: ${post.title}`);
    } catch (error) {
      console.error(`Error generating static file for ${post.title}:`, error);
    }
  }
  
  return generatedFiles;
};

/**
 * Generate a sitemap for blog posts
 * @param posts Array of blog posts
 * @returns The generated sitemap XML
 */
export const generateSitemap = (posts: BlogPost[]): string => {
  const baseUrl = 'https://edgefi.com';
  
  // Create the XML structure
  let sitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url>
    <loc>${baseUrl}/blog</loc>
    <changefreq>weekly</changefreq>
    <priority>0.8</priority>
  </url>`;
  
  // Add each blog post
  for (const post of posts) {
    const postDate = new Date(post.date).toISOString().split('T')[0];
    sitemap += `
  <url>
    <loc>${baseUrl}/blog/${post.slug}</loc>
    <lastmod>${postDate}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.6</priority>
  </url>`;
  }
  
  // Close the sitemap
  sitemap += `
</urlset>`;
  
  return sitemap;
};

/**
 * Generate canonical URLs for blog posts
 * @param post The blog post
 * @returns The canonical URL
 */
export const getCanonicalUrl = (post: BlogPost): string => {
  return `https://edgefi.com/blog/${post.slug}`;
};

/**
 * Generate an AI-assisted blog post using the provided prompts
 * @param title The blog post title
 * @param topic The main topic to write about
 * @param keywords Keywords to include
 * @returns A blog post with AI-generated content
 */
export const generateAIBlogPost = async (
  title: string, 
  topic: string, 
  keywords: string[]
): Promise<Partial<BlogPost>> => {
  try {
    const prompt = `Write a professional blog post about ${topic}. 
    Title: "${title}"
    Keywords to include: ${keywords.join(', ')}
    Use markdown formatting for headings, lists, and other elements.
    Include an introduction, 3-4 main sections, and a conclusion with call to action.`;
    
    console.log("Sending AI generation request with prompt:", prompt.substring(0, 100) + "...");
    
    const { supabase } = await import('@/integrations/supabase/client');
    
    const { data, error } = await supabase.functions.invoke('blog-ai-generate', {
      body: { 
        prompt,
        content: "",
        title,
        tags: keywords.join(', ')
      }
    });
    
    if (error) {
      console.error("Error calling blog-ai-generate function:", error);
      let errorMessage = error.message || JSON.stringify(error);
      
      if (errorMessage.includes("OPENAI_API_KEY") || (typeof error === 'object' && error.code === "MISSING_API_KEY")) {
        toast.error("OpenAI API key not configured", {
          description: "Please check your Supabase Edge Function secrets",
          duration: 5000,
        });
        throw new Error("OpenAI API key not configured. Please check your Supabase Edge Function secrets.");
      } else {
        toast.error("Failed to generate AI content", {
          description: errorMessage.substring(0, 100),
          duration: 5000,
        });
        throw new Error(`Failed to generate AI content: ${errorMessage}`);
      }
    }
    
    console.log("AI response received:", data ? "Success" : "No data");
    
    if (!data || !data.content) {
      toast.error("No content was generated", {
        description: "Please try again with a different prompt",
        duration: 5000,
      });
      throw new Error("No content was generated. Please try again.");
    }
    
    // Generate slug from title
    const slug = title
      .toLowerCase()
      .replace(/[^\w\s]/g, "")
      .replace(/\s+/g, "-");
    
    // Generate excerpt from the first paragraph or so
    const content = data.content;
    const excerpt = content.split("\n\n")[0].replace(/[#*_`]/g, "").slice(0, 250) + "...";
    
    // Generate a cover image for the blog post
    let coverImage;
    let imageAttribution;
    
    try {
      const imageResponse = await generateBlogCoverImage(title);
      if (imageResponse.imageUrl) {
        coverImage = imageResponse.imageUrl;
        
        // Store image attribution
        if (imageResponse.photographer && imageResponse.photographerUrl) {
          imageAttribution = {
            photographer: imageResponse.photographer,
            photographerUrl: imageResponse.photographerUrl
          };
        }
      }
    } catch (imageError) {
      console.error("Error generating cover image:", imageError);
      toast.warning("Could not generate a cover image", {
        description: "Your post will be created without an image",
        duration: 5000,
      });
      // Continue without image if image generation fails
    }
    
    toast.success("Content generated successfully!", { 
      duration: 3000 
    });
    
    return {
      title,
      slug,
      content,
      excerpt,
      tags: keywords,
      coverImage,
      imageAttribution,
      date: new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
    };
  } catch (error) {
    console.error("Error generating AI blog post:", error);
    
    // If we haven't already shown a toast, show a generic one
    if (!error.message?.includes("OpenAI API key") && !error.message?.includes("No content was generated")) {
      toast.error("Error generating blog post", {
        description: "Check console for details",
        duration: 5000,
      });
    }
    
    throw error;
  }
};

/**
 * Generate a complete blog post with AI including title, excerpt, content, tags
 * @param topic The main topic to write about
 * @param imageSource Source for image generation (huggingface or pexels)
 * @returns A complete blog post
 */
export const generateCompleteBlogPost = async (
  topic: string, 
  imageSource: 'huggingface' | 'pexels' = 'huggingface'
): Promise<Partial<BlogPost>> => {
  try {
    console.log("Sending complete blog post generation request with topic:", topic);
    toast.info("Generating complete blog post...", {
      description: "This may take a minute or two",
      duration: 5000,
    });
    
    const { supabase } = await import('@/integrations/supabase/client');
    
    const { data, error } = await supabase.functions.invoke('blog-ai-generate', {
      body: { 
        prompt: topic,
        mode: "complete_post" 
      },
    });
    
    if (error) {
      console.error("Error calling blog-ai-generate function:", error);
      let errorMessage = error.message || JSON.stringify(error);
      
      if (errorMessage.includes("OPENAI_API_KEY") || (typeof error === 'object' && error.code === "MISSING_API_KEY")) {
        toast.error("OpenAI API key not configured", {
          description: "Please check your Supabase Edge Function secrets",
          duration: 5000,
        });
        throw new Error("OpenAI API key not configured. Please check your Supabase Edge Function secrets.");
      } else {
        toast.error("Failed to generate blog post", {
          description: errorMessage.substring(0, 100),
          duration: 5000,
        });
        throw new Error(`Failed to generate blog post: ${errorMessage}`);
      }
    }
    
    if (!data) {
      toast.error("No data returned from blog generation function", {
        description: "Please try again with a different topic",
        duration: 5000,
      });
      throw new Error("No data returned from blog generation function");
    }
    
    console.log("Received complete blog post data with keys:", Object.keys(data).join(", "));
    
    // Generate slug from title
    const slug = data.title
      ? data.title.toLowerCase().replace(/[^\w\s]/g, "").replace(/\s+/g, "-")
      : "";
    
    const blogPost: Partial<BlogPost> = {
      title: data.title,
      slug,
      excerpt: data.excerpt,
      content: data.content,
      tags: data.tags,
      date: new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
    };
    
    // If we have a cover image from the AI generation, use it
    if (data.coverImage) {
      blogPost.coverImage = data.coverImage;
      
      if (data.photographer) {
        blogPost.imageAttribution = {
          photographer: data.photographer,
          photographerUrl: data.photographerUrl
        };
      }
    } else {
      // If no cover image was generated with the blog post, try to generate one separately
      try {
        toast.info("Generating cover image...");
        const imageResult = await generateBlogCoverImage(topic, imageSource);
        blogPost.coverImage = imageResult.imageUrl;
        
        if (imageResult.photographer) {
          blogPost.imageAttribution = {
            photographer: imageResult.photographer,
            photographerUrl: imageResult.photographerUrl
          };
        }
      } catch (imageError) {
        console.error("Error generating cover image as fallback:", imageError);
        toast.warning("Could not generate a cover image", {
          description: "Your post will be created without an image",
          duration: 5000,
        });
        // Continue without an image if generation fails
      }
    }
    
    toast.success("Blog post generated successfully!", {
      description: `"${data.title}" is ready to edit`,
      duration: 3000,
    });
    
    return blogPost;
  } catch (error) {
    console.error("Error generating complete blog post:", error);
    
    // If we haven't already shown a toast, show a generic one
    if (!error.message?.includes("OpenAI API key") && 
        !error.message?.includes("No data returned")) {
      toast.error("Error generating blog post", {
        description: "Check console for details",
        duration: 5000,
      });
    }
    
    throw error;
  }
};

/**
 * Generate a blog cover image using HuggingFace or Pexels
 * @param prompt The prompt for image generation/search
 * @param source Source for image generation (huggingface or pexels)
 * @returns The image URL and attribution info
 */
export const generateBlogCoverImage = async (
  prompt: string,
  source: 'huggingface' | 'pexels' = 'huggingface'
): Promise<{ 
  imageUrl: string; 
  photographer?: string;
  photographerUrl?: string;
  alt?: string;
}> => {
  try {
    console.log(`Sending image ${source === 'huggingface' ? 'generation' : 'search'} request with prompt:`, prompt);
    
    const { supabase } = await import('@/integrations/supabase/client');
    
    const { data, error } = await supabase.functions.invoke('blog-image-generate', {
      body: { prompt, source },
    });
    
    if (error) {
      console.error(`Error calling blog-image-generate function:`, error);
      
      let errorMessage = error.message || JSON.stringify(error);
      if (errorMessage.includes("PEXELS_API_KEY") || errorMessage.includes("HUGGING_FACE_ACCESS_TOKEN")) {
        toast.error("Image API keys not configured", {
          description: `Please check your Supabase Edge Function secrets for ${source} API key`,
          duration: 5000,
        });
      } else {
        toast.error("Failed to generate image", {
          description: "Please try again or use a different source",
          duration: 5000,
        });
      }
      
      throw new Error(`Error calling blog-image-generate function: ${errorMessage}`);
    }
    
    if (!data || !data.imageUrl) {
      toast.error("No image was returned", {
        description: "Please try with a different prompt",
        duration: 5000,
      });
      throw new Error("No image URL was returned");
    }
    
    console.log(`Successfully received image from ${source}:`, 
      data.imageUrl.substring(0, 50) + (data.imageUrl.length > 50 ? '...' : ''));
    
    return {
      imageUrl: data.imageUrl,
      photographer: data.photographer || "AI Generated",
      photographerUrl: data.photographerUrl,
      alt: data.alt || prompt
    };
  } catch (error) {
    console.error(`Error generating image with ${source}:`, error);
    
    // Only show toast if we haven't already shown one
    if (!error.message?.includes("API key") && 
        !error.message?.includes("No image URL")) {
      toast.error(`Failed to generate image with ${source}`, {
        description: "Please try again or use a different source",
        duration: 5000,
      });
    }
    
    throw error;
  }
};
